import { type MeResponse, isAdminRole } from "@seagames/contracts";
import { groupGrants } from "../travel/access";

/**
 * Who sees what on the E-Accreditation pages (M7a). NONE of this decides
 * anything: every card read is scoped in SQL (`cardInScope`) and every write
 * re-checks scope and permission in its own statement. It only picks which
 * tabs and buttons to draw, so nobody clicks into a page of 403s. On the
 * card page the buttons follow the API's own `actions` instead.
 *
 * Who someone IS comes from the active role (bug 7): an ORGANIZATION login
 * is a NOC and gets the NOC view — its own cards, no activation, no history,
 * no QR — whatever its group holds. The host side is admins and OPERATION
 * groups (`card.print`, `card.activate` are OPERATION-only permissions).
 */

type Session = Pick<MeResponse, "active_role" | "group">;

/** The NOC view: an ORGANIZATION session. */
export const isNocView = (me: Session): boolean => me.active_role === "ORGANIZATION";

/** Activate printed cards (`POST /cards/activate`): admins, or a host group with `card.activate`. */
export const canActivateCards = (me: Session): boolean =>
	isAdminRole(me.active_role) || (me.active_role === "OPERATION" && groupGrants(me, "card.activate"));

/** Make print batches and mark them printed: admins, or a host group with `card.print`. */
export const canPrintCards = (me: Session): boolean => isAdminRole(me.active_role) || (me.active_role === "OPERATION" && groupGrants(me, "card.print"));

/** "Find a card" by number or QR (`GET /cards/lookup`): admins, or a host group that prints or activates — a NOC gets 403. */
export const canLookupCards = (me: Session): boolean => canActivateCards(me) || canPrintCards(me);

/** The Print batches tab: whoever prints, and a NOC — which the API shows only the self-print batches the host made for it (0075). */
export const canSeePrintBatches = (me: Session): boolean => canPrintCards(me) || isNocView(me);

/** The Activation history tab: the host side that activates (her "Super Admin only"; ours: whoever activates). */
export const canSeeActivationHistory = canActivateCards;

/** Bulk selection on the Cards list: only when there is a bulk action to take. */
export const canSelectCards = (me: Session): boolean => canActivateCards(me) || canPrintCards(me);
